import * as RadixTooltip from "@radix-ui/react-tooltip"
import clsx from "clsx"
import { useTheme } from "next-themes"
import { useState } from "react"

import { useAboveLayersZIndex } from "@hooks/useLayerStack"

import styles from "./Tooltip.module.scss"
import { TooltipProps } from "./Tooltip.types"

// The design insets start/end-aligned arrows 10px (--size-2_5) from the tooltip's edge.
const ARROW_PADDING = 10

export default function Tooltip(props: TooltipProps) {
    const {
        body,
        isDisabled = false,
        isOpen: controlledIsOpen,
        onOpenChange,
        defaultPlacement,
        defaultAlignment = "center",
        wrapChildren = true,
        children,
        textAlignment = "center",
        bodyOverflow = "wrap",
    } = props

    const { resolvedTheme } = useTheme()
    const reversedThemeClass = resolvedTheme === "dark" ? "light-theme" : "dark-theme"

    const isControlled = typeof controlledIsOpen === "boolean"
    const [internalIsOpen, setInternalIsOpen] = useState(false)
    const isTooltipOpen = isControlled ? controlledIsOpen : internalIsOpen
    const zIndex = useAboveLayersZIndex()

    const handleOpenChange = (next: boolean) => {
        if (!isControlled) {
            setInternalIsOpen(next)
        }
        onOpenChange?.(next)
    }

    if (isDisabled) {
        return <>{children}</>
    } else {
        return (
            <RadixTooltip.Provider>
                <RadixTooltip.Root open={isTooltipOpen} onOpenChange={handleOpenChange}>
                    <RadixTooltip.Trigger asChild={true}>
                        {wrapChildren ? <div>{children}</div> : children}
                    </RadixTooltip.Trigger>

                    <RadixTooltip.Portal>
                        <RadixTooltip.Content
                            side={defaultPlacement}
                            align={defaultAlignment}
                            sideOffset={4}
                            arrowPadding={ARROW_PADDING}
                            style={{ zIndex }}
                            className={clsx(styles.base, reversedThemeClass)}
                        >
                            {typeof body === "string" ? (
                                <span
                                    className={clsx(
                                        styles.text,
                                        styles[`${textAlignment}Text`],
                                        bodyOverflow === "truncate" && styles.truncate,
                                    )}
                                >
                                    {body}
                                </span>
                            ) : (
                                body
                            )}
                            <RadixTooltip.Arrow width={8} height={4} className={styles.arrow} />
                        </RadixTooltip.Content>
                    </RadixTooltip.Portal>
                </RadixTooltip.Root>
            </RadixTooltip.Provider>
        )
    }
}
